import React from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import { ChevronRight, Layers } from "lucide-react";
import {
  ConsolidationChangeStatus,
  OutboundChange,
} from "@/models/legislation/metadata/ConsolidationMetadata";
import { TimelineItem } from "@/models/legislation/Timeline";
import {
  buildOutboundConsolidationPath,
  getOutboundProgress,
} from "@/lib/consolidationMetadata";
import { resolveTimelineLabel } from "@/lib/consolidationViewerUtils";

const STATUS_LABELS: Record<ConsolidationChangeStatus, string> = {
  APPROVED: "Godkendt",
  NEED_REVIEW: "Kræver gennemgang",
};

const STATUS_VARIANT: Record<
  ConsolidationChangeStatus,
  "default" | "secondary" | "outline"
> = {
  APPROVED: "default",
  NEED_REVIEW: "secondary",
};

interface OutboundConsolidationsPanelProps {
  outboundChanges: OutboundChange[];
  changelawExternalId: string;
  timelineItems?: TimelineItem[];
}

const OutboundConsolidationsPanel: React.FC<OutboundConsolidationsPanelProps> = ({
  outboundChanges,
  changelawExternalId,
  timelineItems,
}) => {
  const navigate = useNavigate();
  const progress = getOutboundProgress(outboundChanges);

  if (outboundChanges.length === 0) return null;

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-2 pb-1 no-print">
      <div className="rounded-md border border-border bg-muted/20 overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border/60 bg-muted/30">
          <Layers className="size-4 text-muted-foreground shrink-0" />
          <span className="text-sm font-medium text-foreground">
            Udløste konsolideringer
          </span>
          <span className="text-xs text-muted-foreground ml-auto">
            {progress.approved}/{progress.total} godkendt
          </span>
        </div>
        <ul className="divide-y divide-border/60">
          {outboundChanges.map((entry) => {
            const label = resolveTimelineLabel(
              entry.anchorLawAccession,
              timelineItems
            );

            return (
              <li key={entry.anchorLawAccession}>
                <button
                  type="button"
                  id={`outbound-consolidation-${entry.anchorLawAccession}`}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-muted/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() =>
                    navigate(
                      buildOutboundConsolidationPath(
                        entry.anchorLawAccession,
                        changelawExternalId
                      )
                    )
                  }
                  aria-label={`Åbn konsolideret tekst for ${label}`}
                >
                  <span className="flex-1 min-w-0 truncate text-foreground">
                    {label}
                  </span>
                  <Badge variant={STATUS_VARIANT[entry.status]}>
                    {STATUS_LABELS[entry.status]}
                  </Badge>
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default OutboundConsolidationsPanel;
